// src/components/pages/blotter/converts/EditBlotterEntry.js
import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchBlotterEntryById,
  updateBlotterEntry,
} from "../../../../services/Axios";

const ACTION_OPTIONS = ["Buy", "Sell", "Short", "Cover"];

export default function EditBlotterEntry() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    trade_date: "",
    strategy_name: "",
    security_symbol: "",
    hedge_symbol: "",
    action: "",
    hedge_quantity: "",
    hedge_price: "",
    hedge_currency: "",
    notes: {},
    enter_hedge: false,
    hedge_entered: false,
    not_convert: false,
    entered_manually: false,
  });

  const [notes, setNotes] = useState([{ key: "", value: "" }]);
  const [showNotes, setShowNotes] = useState(false);
  const [hedgeAction, setHedgeAction] = useState("");
  const [hedgeEntryText, setHedgeEntryText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Load existing data
  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchBlotterEntryById(id);
        if (data) {
          setFormData(data);

          // Convert notes object to array of key-value pairs
          if (data.notes && typeof data.notes === "object") {
            const notesArray = Object.entries(data.notes).map(
              ([key, value]) => ({ key, value }),
            );
            setNotes(
              notesArray.length > 0 ? notesArray : [{ key: "", value: "" }],
            );
            // Show notes section if there are existing notes
            setShowNotes(notesArray.length > 0);
          }
        }
      } catch (err) {
        setError("Failed to load blotter entry: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  // Calculate hedge action based on security action
  useEffect(() => {
    const getHedgeAction = (action) => {
      switch (action) {
        case "Buy":
          return "Short";
        case "Sell":
          return "Cover";
        case "Short":
          return "Buy";
        case "Cover":
          return "Sell";
        default:
          return "";
      }
    };

    const action = getHedgeAction(formData.action);
    setHedgeAction(action);

    if (
      action &&
      formData.hedge_quantity &&
      formData.hedge_symbol &&
      formData.hedge_price
    ) {
      setHedgeEntryText(
        `${action} ${formData.hedge_quantity} of ${formData.hedge_symbol} at ${formData.hedge_price} ${formData.hedge_currency}`,
      );
    } else {
      setHedgeEntryText("");
    }
  }, [
    formData.action,
    formData.hedge_quantity,
    formData.hedge_symbol,
    formData.hedge_price,
  ]);

  const handleInputChange = (field, value) => {
    // Normalize text by uppercasing string fields
    const normalizedValue =
      typeof value === "string" && field !== "trade_date"
        ? value.toUpperCase()
        : value;
    setFormData((prev) => ({
      ...prev,
      [field]: normalizedValue,
    }));
  };

  const handleNotesChange = (index, field, value) => {
    const newNotes = [...notes];
    newNotes[index][field] = value.toUpperCase();
    setNotes(newNotes);
  };

  const addNoteField = () => {
    setNotes([...notes, { key: "", value: "" }]);
  };

  const removeNoteField = (index) => {
    if (notes.length > 1) {
      const newNotes = notes.filter((_, i) => i !== index);
      setNotes(newNotes);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSaving(true);
    setError("");

    try {
      // Convert notes array to object
      const notesObject = {};
      notes.forEach((note) => {
        if (note.key && note.value) {
          notesObject[note.key] = note.value;
        }
      });

      const submitData = {
        ...formData,
        notes: notesObject,
      };

      await updateBlotterEntry(id, submitData);
      navigate("/Converts_Blotter");
    } catch (err) {
      setError("Failed to update blotter entry: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/Converts_Blotter");
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0.5, sm: 1 }, maxWidth: 1400, mx: "auto", mt: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 1 }}>
        Edit Blotter Entry
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={1}>
          {/* Main Form Fields */}
          <Grid item xs={12}>
            <Grid container spacing={1}>
              {/* Convertible Bond */}
              <Grid item xs={12}>
                <Card sx={{ mb: 1 }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ mb: 1, fontSize: "1rem" }}
                    >
                      Convertible Bond
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Trade Date"
                          type="date"
                          value={formData.trade_date}
                          onChange={(e) =>
                            handleInputChange("trade_date", e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                          required
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Strategy Name"
                          value={formData.strategy_name}
                          onChange={(e) =>
                            handleInputChange("strategy_name", e.target.value)
                          }
                          required
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small" required>
                          <InputLabel>Action</InputLabel>
                          <Select
                            value={formData.action}
                            label="Action"
                            onChange={(e) =>
                              handleInputChange("action", e.target.value)
                            }
                            disabled
                          >
                            {ACTION_OPTIONS.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Security Symbol"
                          value={formData.security_symbol}
                          onChange={(e) =>
                            handleInputChange("security_symbol", e.target.value)
                          }
                          required
                          disabled
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Hedge Information */}
              <Grid item xs={12}>
                <Card sx={{ mb: 1 }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ mb: 1, fontSize: "1rem" }}
                    >
                      Hedge Information
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Hedge Symbol"
                          value={formData.hedge_symbol}
                          onChange={(e) =>
                            handleInputChange("hedge_symbol", e.target.value)
                          }
                          required
                          disabled={
                            formData.not_convert || formData.entered_manually
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              border:
                                !formData.not_convert &&
                                !formData.entered_manually &&
                                !formData.hedge_symbol
                                  ? "2px solid #d32f2f"
                                  : "1px solid rgba(0, 0, 0, 0.23)",
                              "&:hover": {
                                border:
                                  !formData.not_convert &&
                                  !formData.entered_manually &&
                                  !formData.hedge_symbol
                                    ? "2px solid #d32f2f"
                                    : "1px solid rgba(0, 0, 0, 0.87)",
                              },
                              "&.Mui-focused": {
                                border:
                                  !formData.not_convert &&
                                  !formData.entered_manually &&
                                  !formData.hedge_symbol
                                    ? "2px solid #d32f2f"
                                    : "2px solid #1976d2",
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Hedge Quantity"
                          type="number"
                          value={formData.hedge_quantity}
                          onChange={(e) =>
                            handleInputChange("hedge_quantity", e.target.value)
                          }
                          required
                          disabled={
                            formData.not_convert || formData.entered_manually
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              border:
                                !formData.not_convert &&
                                !formData.entered_manually &&
                                !formData.hedge_quantity
                                  ? "2px solid #d32f2f"
                                  : "1px solid rgba(0, 0, 0, 0.23)",
                              "&:hover": {
                                border:
                                  !formData.not_convert &&
                                  !formData.entered_manually &&
                                  !formData.hedge_quantity
                                    ? "2px solid #d32f2f"
                                    : "1px solid rgba(0, 0, 0, 0.87)",
                              },
                              "&.Mui-focused": {
                                border:
                                  !formData.not_convert &&
                                  !formData.entered_manually &&
                                  !formData.hedge_quantity
                                    ? "2px solid #d32f2f"
                                    : "2px solid #1976d2",
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Hedge Price"
                          type="number"
                          step="0.01"
                          value={formData.hedge_price}
                          onChange={(e) =>
                            handleInputChange("hedge_price", e.target.value)
                          }
                          required
                          disabled={
                            formData.not_convert || formData.entered_manually
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              border:
                                !formData.not_convert &&
                                !formData.entered_manually &&
                                !formData.hedge_price
                                  ? "2px solid #d32f2f"
                                  : "1px solid rgba(0, 0, 0, 0.23)",
                              "&:hover": {
                                border:
                                  !formData.not_convert &&
                                  !formData.entered_manually &&
                                  !formData.hedge_price
                                    ? "2px solid #d32f2f"
                                    : "1px solid rgba(0, 0, 0, 0.87)",
                              },
                              "&.Mui-focused": {
                                border:
                                  !formData.not_convert &&
                                  !formData.entered_manually &&
                                  !formData.hedge_price
                                    ? "2px solid #d32f2f"
                                    : "2px solid #1976d2",
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Hedge Currency"
                          value={formData.hedge_currency}
                          onChange={(e) =>
                            handleInputChange("hedge_currency", e.target.value)
                          }
                          required
                          disabled={
                            formData.not_convert || formData.entered_manually
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              border:
                                !formData.not_convert &&
                                !formData.entered_manually &&
                                !formData.hedge_currency
                                  ? "2px solid #d32f2f"
                                  : "1px solid rgba(0, 0, 0, 0.23)",
                              "&:hover": {
                                border:
                                  !formData.not_convert &&
                                  !formData.entered_manually &&
                                  !formData.hedge_currency
                                    ? "2px solid #d32f2f"
                                    : "1px solid rgba(0, 0, 0, 0.87)",
                              },
                              "&.Mui-focused": {
                                border:
                                  !formData.not_convert &&
                                  !formData.entered_manually &&
                                  !formData.hedge_currency
                                    ? "2px solid #d32f2f"
                                    : "2px solid #1976d2",
                              },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Notes Section */}
              {showNotes && (
                <Grid item xs={12}>
                  <Card sx={{ mb: 1 }}>
                    <CardContent sx={{ p: 1.5 }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ mb: 1, fontSize: "1rem" }}
                      >
                        Notes (Key-Value Pairs)
                      </Typography>
                      {notes.map((note, index) => (
                        <Box
                          key={index}
                          sx={{ display: "flex", gap: 1, mb: 1 }}
                        >
                          <TextField
                            size="small"
                            label="Key"
                            value={note.key}
                            onChange={(e) =>
                              handleNotesChange(index, "key", e.target.value)
                            }
                            sx={{ flex: 1 }}
                            disabled={
                              formData.not_convert || formData.entered_manually
                            }
                          />
                          <TextField
                            size="small"
                            label="Value"
                            value={note.value}
                            onChange={(e) =>
                              handleNotesChange(index, "value", e.target.value)
                            }
                            sx={{ flex: 1 }}
                            disabled={
                              formData.not_convert || formData.entered_manually
                            }
                          />
                          <IconButton
                            onClick={() => removeNoteField(index)}
                            disabled={
                              notes.length === 1 ||
                              formData.not_convert ||
                              formData.entered_manually
                            }
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        startIcon={<AddIcon />}
                        onClick={addNoteField}
                        variant="outlined"
                        size="small"
                        disabled={
                          formData.not_convert || formData.entered_manually
                        }
                      >
                        Add Note Field
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Boolean Settings */}
              <Grid item xs={12}>
                <Card sx={{ mb: 1 }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ mb: 1, fontSize: "1rem" }}
                    >
                      Settings
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.not_convert}
                              onChange={(e) =>
                                handleInputChange(
                                  "not_convert",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label="Not Convert"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.enter_hedge}
                              onChange={(e) =>
                                handleInputChange(
                                  "enter_hedge",
                                  e.target.checked,
                                )
                              }
                              disabled={
                                formData.not_convert ||
                                formData.entered_manually
                              }
                            />
                          }
                          label="Enter Hedge"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={showNotes}
                              onChange={(e) => setShowNotes(e.target.checked)}
                              disabled={
                                formData.not_convert ||
                                formData.entered_manually
                              }
                            />
                          }
                          label="View Notes"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.entered_manually}
                              onChange={(e) =>
                                handleInputChange(
                                  "entered_manually",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label="Entered Manually"
                        />
                      </Grid>
                      {hedgeEntryText && (
                        <Grid item xs={12}>
                          <Alert severity="info">
                            <Typography variant="subtitle2" gutterBottom>
                              Hedge Action:
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: "bold" }}
                            >
                              {hedgeEntryText}
                            </Typography>
                          </Alert>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={saving}
                size="small"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                size="small"
                sx={{
                  minWidth: "200px",
                  backgroundColor: theme.palette.primary.dark,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}
